import { NavItemsSimple } from '@/components/custom/nav-Items-simple'
import WorkspaceSidebarScaffold from '@/features/workspace/components/WorkspaceSidebarScaffold'
import { Sidebar } from '@/shared/components/ui/sidebar'
import React, { useState } from 'react'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useStoryboardStore } from '../store/useStoryboardStore'
import StoryboardGenerationDialog from './StoryboardGenerationDialog'
import { downloadStoryboardPDF } from './pdf/utils'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { useStoryboardQuery } from '../queries/storyboard.queries'
import { useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { storyboardRepository } from '../repository/StoryboardRepository'
import { shotListRepository } from '@/features/shotlist/repository/ShotListRepository'
import { toast } from 'sonner'

export function SbSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {

    const [isGenerationDialogOpen, setIsGenerationDialogOpen] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    const { project } = useWorkspace()
    const { selectedSceneNode } = useStoryboardStore()
    const { editor } = useSceneStore()
    const { shotLists } = useShotListStore()

    const { data: frames = [] } = useStoryboardQuery(project?.id || '', selectedSceneNode?.id ?? null)
    const currentSceneShots = selectedSceneNode ? shotLists[selectedSceneNode.id] || [] : []

    const handleDownloadScene = async () => {
        if (!project || !selectedSceneNode) return
        
        if (frames.length === 0) {
            toast.error("No storyboards in this scene to download.")
            return
        }

        setIsDownloading(true)
        try {
            const sceneTitle = editor?.read(() => getSceneHeader(selectedSceneNode)) || 'Unknown Scene'
            const fileName = `${project.title} - ${sceneTitle} - Storyboard`
            
            await downloadStoryboardPDF(fileName, project.title, sceneTitle, frames, currentSceneShots)
        } catch (error) {
            console.error("Failed to download scene PDF", error)
            toast.error("Failed to generate PDF")
        } finally {
            setIsDownloading(false)
        }
    }

    const handleDownloadProject = async () => {
        if (!project) return

        setIsDownloading(true)
        try {
            const allStoryboards = await storyboardRepository.getByProjectId(project.id)
            const allShotlists = await shotListRepository.getByProjectId(project.id)
            
            const allFrames = allStoryboards.flatMap(s => s.frames)
            const allShots = allShotlists.flatMap(s => s.shots)

            if (allFrames.length === 0) {
                toast.error("No storyboards in this project to download.")
                setIsDownloading(false)
                return
            }

            const fileName = `${project.title} - Full Storyboard`
            await downloadStoryboardPDF(fileName, project.title, undefined, allFrames, allShots)
        } catch (error) {
            console.error("Failed to download project PDF", error)
            toast.error("Failed to generate PDF")
        } finally {
            setIsDownloading(false)
        }
    }

    const navItems = [
        {
            name: 'Download Project (PDF)',
            emoji: isDownloading ? '⏳' : '📄',
            onClick: handleDownloadProject,
            disabled: isDownloading || !project
        },
        {
            name: 'Download Scene (PDF)',
            emoji: isDownloading ? '⏳' : '🎬',
            onClick: handleDownloadScene,
            disabled: isDownloading || !project || !selectedSceneNode || frames.length === 0
        }
    ]

    return (
        <>
            <WorkspaceSidebarScaffold>
                <NavItemsSimple navItems={navItems} header={{ title: 'Storyboard' }} />
            </WorkspaceSidebarScaffold>

            <StoryboardGenerationDialog
                open={isGenerationDialogOpen}
                onOpenChange={setIsGenerationDialogOpen}
            />
        </>
    )
}

export default SbSidebar
